import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Package,
  ShoppingCart,
  IndianRupee,
  Layers,
  ArrowRight,
  Star,
  Loader2,
  Package2,
} from "lucide-react";
import { gruhUdhyogDashboardService } from "@project/services/admin/gruh-udhyog-dashboard.service";
import type { GruhUdhyogDashboardData } from "@project/services/admin/gruh-udhyog-dashboard.service";
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export default function GruhUdhyogDashboard() {
  const [data, setData] = useState<GruhUdhyogDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadDashboard() {
      try {
        setLoading(true);
        const res = await gruhUdhyogDashboardService.fetch();
        setData(res);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, []);

  if (loading) {
    return (
      <AdminShell
        title="Gruh Udhyog"
        subtitle="Home-industry e-commerce snapshot"
      >
        <div className="flex h-[400px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminShell>
    );
  }

  if (error || !data) {
    return (
      <AdminShell
        title="Gruh Udhyog"
        subtitle="Home-industry e-commerce snapshot"
      >
        <div className="flex h-[400px] flex-col items-center justify-center gap-4 text-center">
          <p className="text-destructive font-medium">
            {error || "Failed to load dashboard."}
          </p>
          <Button onClick={() => window.location.reload()} size="sm">
            Retry
          </Button>
        </div>
      </AdminShell>
    );
  }

  const { summary, revenue_snapshot = [], categories = [], recent_orders = [], top_rated = [] } = data;

  const totalProducts = summary?.products ?? 0;
  const totalOrders = summary?.orders_7_days ?? 0;
  const pendingOrders = summary?.orders_to_fulfil ?? 0;
  const revenue7d = parseFloat(summary?.revenue_7_days as string) || 0;
  const totalCategories = summary?.categories ?? 0;

  const chartData = revenue_snapshot.map(item => ({
    day: item.day,
    revenue: parseFloat(item.amount as string) || 0,
    date: item.date,
  }));

  return (
    <AdminShell
      title="Gruh Udhyog"
      subtitle="Home-industry e-commerce snapshot"
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/gruh-udhyog/products">
            Product Listing <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Products"
          value={totalProducts}
          icon={<Package className="h-5 w-5 text-white" />}
          accent="from-primary to-primary"
          sub={`${totalCategories} categories`}
        />
        <StatCard
          label="Orders (7 days)"
          value={totalOrders}
          icon={<ShoppingCart className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub={`${pendingOrders} to fulfil`}
        />
        <StatCard
          label="Revenue (7 days)"
          value={`₹${revenue7d.toLocaleString("en-IN")}`}
          icon={<IndianRupee className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub="Net of refunds"
        />
        <StatCard
          label="Categories"
          value={totalCategories}
          icon={<Layers className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub="Active product types"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Revenue Snapshot"
          subtitle="Last 7 days · ₹"
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--primary)"
                    stopOpacity={1}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--primary)"
                    stopOpacity={0.5}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                formatter={(v: any) => [
                  `₹${Number(v).toLocaleString("en-IN")}`,
                  "Revenue",
                ]}
                cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              />
              <Bar dataKey="revenue" fill="url(#gRev)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Product Categories" subtitle="Distribution">
          <div className="space-y-2">
            {categories.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center text-center text-sm text-muted-foreground">
                No categories available
              </div>
            ) : (
              categories.map(c => {
                const count = c.product_count;
                const pct = Math.round(c.percentage);
                return (
                  <div
                    key={c.name}
                    className="bg-muted/30 rounded-lg border border-border p-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        {c.name}
                      </span>
                      <Badge variant="secondary">{count} items</Badge>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </SectionCard>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Orders Summary"
          subtitle="Latest customer orders"
          className="lg:col-span-2"
        >
          <div className="divide-y divide-border">
            {recent_orders.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center text-center text-sm text-muted-foreground">
                No orders yet
              </div>
            ) : (
              recent_orders.map(o => (
                <div
                  key={o.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {o.order_number || `ORD-${o.id}`} · {o.customer_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {o.items_count} {o.items_count === 1 ? "item" : "items"} · {o.date} {o.time}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <div className="font-semibold text-foreground">
                      ₹{parseFloat(o.amount as string).toLocaleString("en-IN")}
                    </div>
                    <Badge
                      variant={o.status === "Delivered" ? "secondary" : "outline"}
                    >
                      {o.status}
                    </Badge>
                  </div>
                </div>
              )))}
          </div>
        </SectionCard>

        <SectionCard title="Top Rated" subtitle="By customer feedback">
          <div className="space-y-2">
            {top_rated.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center text-center text-sm text-muted-foreground">
                No rated products yet
              </div>
            ) : (
              top_rated.map((p: any) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-card p-2.5"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {p.product_name || p.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {p.category_name || p.category}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 text-sm font-semibold text-amber-600">
                    <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />{" "}
                    {Number(p.rating || 0).toFixed(1)}
                  </div>
                </div>
              ))
            )}
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
